import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import { AuthProvider } from "./components/contexts/AuthContext/AuthContext";
import { useAuth } from "./components/contexts/AuthContext/UseAuth";
import { Layout } from "./components/Layout";
import { LoadingDots } from "./components/ui/LoadingDots";
import { LeadsPage } from "./pages/LeadsPage";
import { WelcomePage } from "./pages/WelcomePage";

// Identity pages
import LoginErrorPage from "./pages/Identity/LoginError";

// Auth callback pages
import LoginCallbackPage from "./pages/Auth/LoginCallback";
import LogoutCallbackPage from "./pages/Auth/LogoutCallback";

const queryClient = new QueryClient();

const SentryRoutes = Sentry.withSentryReactRouterV7Routing(Routes);

const LoadingScreen = () => (
  <div className="flex justify-center items-center h-screen">
    <LoadingDots />
  </div>
);

export const App = () => {
  return (
    <Sentry.ErrorBoundary
      fallback={
        <div className="h-[100dvh] w-full flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Something went wrong</h1>
            <p className="text-gray-600">
              Please refresh the page or contact support if the problem
              persists.
            </p>
          </div>
        </div>
      }
    >
      <QueryClientProvider client={queryClient}>
        <AuthProvider loadingElement={<LoadingScreen />}>
          <SentryRoutes>
            {/* Public routes */}
            <Route
              path="/signin-error"
              element={
                <PublicOnlyRoute>
                  <LoginErrorPage />
                </PublicOnlyRoute>
              }
            />
            <Route path="/callback" element={<LoginCallbackPage />} />
            <Route path="/logout-callback" element={<LogoutCallbackPage />} />

            {/* Protected routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<WelcomePage />} />
              <Route path="leads" element={<LeadsPage />} />
            </Route>
          </SentryRoutes>
        </AuthProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

const PublicOnlyRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated, login } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    console.log("Not authenticated, redirecting to login...");
    login(location.pathname + location.search);
    return null;
  }

  return children;
};
